import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FiCamera, FiX, FiXCircle } from "react-icons/fi";
import { Box, Typography } from "@material-ui/core";

import {
  StyledBottomImage,
  StyledImageDrop,
  StyledImagePreview,
  StyledMutedText,
  StyledRemoveButton,
  StyledUploadOverlay,
} from "../styles";
import { PrimaryButton } from "../../globalComponents/Buttons";
import { getBase64 } from "../../utils/getBase64";

export const ItemImageComponent = (props) => {
  const [activeImage, setActiveImage] = useState(0);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const filesToProcess = acceptedFiles.slice(
        0,
        3 - props?.imageArr?.length
      );

      try {
        const newImagePromises = filesToProcess.map((file) => getBase64(file));
        const newImages = await Promise.all(newImagePromises);

        // Extract only the base64 string without the data URL prefix
        const base64Strings = newImages.map((imageDataUrl) => {
          const base64String = imageDataUrl.split(",")[1];
          return base64String;
        });

        const updatedImages = [...props.imageArr, ...base64Strings];
        props.getImage(updatedImages);
      } catch (error) {
        console.error("Error processing images:", error);
      }
    },
    [props.imageArr, props.getImage]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*",
    maxFiles: 3 - props.imageArr.length,
    disabled: props.imageArr.length >= 3,
  });

  const removeImage = (indexToRemove) => {
    const updatedImages = props.imageArr.filter(
      (_, index) => index !== indexToRemove
    );
    props.getImage(updatedImages);

    // Adjust active image if necessary
    if (activeImage >= updatedImages.length && updatedImages.length > 0) {
      setActiveImage(updatedImages.length - 1);
    } else if (updatedImages.length === 0) {
      setActiveImage(0);
    }
  };

  const imageList = props.imageArr.map((item, index) => {
    // Convert base64 string back to data URL for display
    const imageUrl = item.startsWith("http:")
      ? item
      : `data:image/jpeg;base64,${item}`;

    return (
      <StyledBottomImage
        key={index}
        onClick={() => setActiveImage(index)}
        isActive={activeImage === index}
      >
        <img src={imageUrl} alt={`Preview ${index + 1}`} />
        <StyledRemoveButton
          onClick={(e) => {
            e.stopPropagation();
            removeImage(index);
          }}
        >
          {/* <FiX /> */}
          <FiXCircle />
        </StyledRemoveButton>
      </StyledBottomImage>
    );
  });

  // Get the active image URL for preview
  const getActiveImageUrl = () => {
    if (props.imageArr.length === 0) return "";
    const activeImageData = props.imageArr[activeImage];
    return activeImageData.startsWith("http:")
      ? activeImageData
      : `data:image/jpeg;base64,${activeImageData}`;
  };

  const canUploadMore = props.imageArr.length < 3;

  return (
    <>
      <Box px={3} pt={2}>
        <StyledImageDrop
          isImage={props.imageArr.length}
          {...(canUploadMore ? getRootProps() : {})}
          disabled={!canUploadMore}
        >
          {canUploadMore && <input {...getInputProps()} />}
          {isDragActive ? (
            <p>Drop the image(s) here ...</p>
          ) : !props.imageArr.length ? (
            <>
              <FiCamera />
              <StyledMutedText variant="body1">
                Drag image to upload
              </StyledMutedText>
              <Box mt={2}>
                <PrimaryButton text="Choose Image" />
              </Box>
            </>
          ) : (
            <StyledImagePreview>
              <img src={getActiveImageUrl()} alt="Preview" />
              <Box position="relative">
                {canUploadMore && (
                  <StyledUploadOverlay>
                    <FiCamera />
                    <Typography variant="caption">
                      Click to add ({props.imageArr.length}/3)
                    </Typography>
                  </StyledUploadOverlay>
                )}
              </Box>
            </StyledImagePreview>
          )}
        </StyledImageDrop>
      </Box>

      {!props.hideFooterText && (
        <Box px={3}>
          <Typography variant="caption">
            You can add up to 3 images ({props.imageArr.length}/3)
          </Typography>
        </Box>
      )}

      {props.imageArr.length > 0 && (
        <Box mt={3} px={3} display="flex" alignItems="center" gap={1}>
          {imageList}
        </Box>
      )}
    </>
  );
};
